import { useEffect, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";

export default function Algorithm() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const { showModal } = useMessageModal();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  /* =========================
     FETCH ALGORITHMS
  ========================= */

  const fetchAlgorithms = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/settings/algorithm`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Algorithm Error:", error);
    }
  };

  useEffect(() => {
    fetchAlgorithms();
  }, []);

  /* =========================
     ADD / UPDATE
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/settings/algorithm`
          : `${BASE_URL}/admin/settings/algorithm/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (result.success) {
        fetchAlgorithms();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Public email added successfully."
              : "Public email updated successfully.",
        });
      }
    } catch (error) {
      console.error("Submit Error:", error);
    }
  };

  /* =========================
     DELETE
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Algorithm",
      message: "Are you sure you want to delete this algorithm?",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/settings/algorithm/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchAlgorithms();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Algorithm deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  /* =========================
     FORM FIELDS
  ========================= */

  const algorithmFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },
    { key: "name", label: "Algorithm Name", colSpan: 2 },
    { key: "algorithm_key", label: "Parameter", colSpan: 2 },
    { key: "algorithm_value", label: "Value", colSpan: 2 },
    { key: "status", label: "Status", colSpan: 2 },
  ];

  const columns = [
    { key: "name", label: "Algorithm Name" },
    { key: "algorithm_key", label: "Parameter" },
    { key: "algorithm_value", label: "Value" },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Algorithm Setup"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: any) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.name}</h3>

                <p>
                  <strong>{item.algorithm_key}:</strong> {item.algorithm_value}
                </p>

                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={
                      item.status === "active"
                        ? "badge-success"
                        : "badge-danger"
                    }
                  >
                    {item.status?.charAt(0).toUpperCase() +
                      item.status?.slice(1)}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Algorithm"
        fields={algorithmFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
